import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "HOME", href: "#home", section: true },
  { label: "ABOUT US", href: "#about", section: true },
  { label: "SERVICES", href: "#services", section: true },
  { label: "UPDATES", to: "/updates" },
  { label: "CONTACT US", to: "/contact" },
  { label: "GET A QUOTE", href: "#quote", section: true },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSection = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (loc.pathname !== "/") {
      nav(`/${href}`);
      return;
    }
    setTimeout(() => {
      const el = document.querySelector(href);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth" });
    }, 0);
  };

  // Solid red at top → semi-transparent red once user scrolls
  const bg = "#e3000f";
  const backdropFilter = scrolled ? "blur(14px) saturate(1.4)" : "none";

  return (
    <header
      className="w-full z-50 transition-all duration-300"
      style={{
        position: "sticky",
        top: 0,
        background: bg,
        backdropFilter,
        WebkitBackdropFilter: backdropFilter,
        boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.18)" : "none",
      }}
      data-testid="public-nav"
    >
      <div
        className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between transition-all duration-300"
        style={{ height: scrolled ? 68 : 84 }}
      >
        <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link" onClick={() => setOpen(false)}>
          <div
            className="relative inline-flex items-center justify-center transition-all duration-300"
            style={{ height: scrolled ? 52 : 68, width: scrolled ? 52 : 190 }}
          >
            <img
              src="/assets/TRANSLOG SECONDARY LOGO.png"
              alt="Translog Express Pvt. Ltd."
              className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300"
              style={{ opacity: scrolled ? 0 : 1, filter: "brightness(0) invert(1)" }}
            />
            <img
              src="/assets/TRANSLOG LOGO MARK 2.png"
              alt="Translog Express"
              className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300"
              style={{ opacity: scrolled ? 1 : 0 }}
            />
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((l) =>
            l.section ? (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => handleSection(e, l.href)}
                className="text-sm font-semibold transition-colors"
                style={{ color: "rgba(255,255,255,0.88)" }}
                onMouseEnter={e => e.currentTarget.style.color = "#ffffff"}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.88)"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-sm font-semibold transition-colors"
                style={{ color: "rgba(255,255,255,0.88)" }}
                onMouseEnter={e => e.currentTarget.style.color = "#ffffff"}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.88)"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                {l.label}
              </Link>
            )
          )}
          <Link
            to="/login"
            className="text-sm font-bold px-4 py-2 rounded-lg border-2 border-white transition-all"
            style={{ color: "#DC2A1C", background: "#ffffff" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.88)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#ffffff"; }}
            data-testid="nav-login-btn"
          >
            Employee Login
          </Link>
        </nav>

        <button
          className="lg:hidden p-2"
          style={{ color: "#ffffff" }}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden"
          style={{
            background: scrolled ? "rgba(220, 42, 28, 0.88)" : "#dc2a1c",
            backdropFilter,
            WebkitBackdropFilter: backdropFilter,
            borderTop: "1px solid rgba(255,255,255,0.15)",
          }}
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((l) =>
              l.section ? (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => handleSection(e, l.href)}
                  className="py-2.5 font-semibold text-sm"
                  style={{ color: "rgba(255,255,255,0.9)" }}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-2.5 font-semibold text-sm"
                  style={{ color: "rgba(255,255,255,0.9)" }}
                >
                  {l.label}
                </Link>
              )
            )}
            <Link
              to="/login"
              className="mt-3 text-center text-sm font-bold px-4 py-2.5 rounded-lg border-2 border-white"
              style={{ color: "#DC2A1C", background: "#ffffff" }}
              onClick={() => setOpen(false)}
            >
              Employee Login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
